import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CoachLayout } from '../components/CoachLayout';
import { venuesAPI, lessonsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadPDF } from '../lib/pdfDownload';
import { useAuth } from '../context/AuthContext';

const ITEMS_PER_PAGE = 10;

export default function VenueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPageLessons, setCurrentPageLessons] = useState(1);
  const [currentPageAthletes, setCurrentPageAthletes] = useState(1);
  const [expandedAthlete, setExpandedAthlete] = useState(null);

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const response = await venuesAPI.getDetails(id);
      setData(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
      navigate('/venues');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/venues?edit=${id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questa struttura?')) return;
    try {
      await venuesAPI.delete(id);
      toast.success('Struttura eliminata');
      navigate('/venues');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const markAsPaid = async (lessonId, athleteId) => {
    try {
      await lessonsAPI.markPayment({
        lesson_id: lessonId,
        athlete_id: athleteId,
        payment_type: 'venue',
      });
      toast.success('Pagamento struttura marcato come pagato!');
      fetchDetails();
    } catch (error) {
      toast.error('Errore nel marcare il pagamento');
    }
  };

  const unmarkAsPaid = async (lessonId, athleteId) => {
    try {
      await lessonsAPI.unmarkPayment({
        lesson_id: lessonId,
        athlete_id: athleteId,
        payment_type: 'venue',
      });
      toast.success('Pagamento struttura annullato!');
      fetchDetails();
    } catch (error) {
      toast.error("Errore nell'annullare il pagamento");
    }
  };

  const exportFullPDF = async () => {
    if (!data) return;

    const toastId = 'export-full-pdf';
    try {
      toast.loading('Creo il report PDF...', { id: toastId });

      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.text(`Trainers' Manager - Report Affitti: ${data.venue.name}`, 14, 20);

      doc.setFontSize(12);
      doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);
      doc.text(`Totale Lezioni: ${data.summary.total_lessons}`, 14, 42);
      doc.text(`Totale Affitti: € ${data.summary.total_venue_cost.toFixed(2)}`, 14, 49);
      doc.text(`Pagato: € ${data.summary.paid_venue_cost.toFixed(2)}`, 14, 56);
      doc.text(`Da Pagare: € ${data.summary.unpaid_venue_cost.toFixed(2)}`, 14, 63);

      // Tabella riepilogo per atleta
      doc.setFontSize(14);
      doc.text('Riepilogo per Atleta', 14, 77);

      const athleteTableData = [...data.athlete_costs]
        .sort((a, b) => a.athlete_name.localeCompare(b.athlete_name))
        .map((a) => [
          a.athlete_name,
          String(a.lessons_count),
          `€ ${a.total_venue_cost.toFixed(2)}`,
          `€ ${a.paid_venue_cost.toFixed(2)}`,
          `€ ${(a.total_venue_cost - a.paid_venue_cost).toFixed(2)}`,
        ]);

      autoTable(doc, {
        head: [['Atleta', 'Lezioni', 'Totale', 'Pagato', 'Da Pagare']],
        body: athleteTableData,
        startY: 82,
        theme: 'grid',
        headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
        styles: { fontSize: 9 },
      });

      // Tabella dettaglio lezioni
      doc.addPage();
      doc.setFontSize(14);
      doc.text('Dettaglio Lezioni', 14, 20);
      doc.setFontSize(12);
      doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);

      const lessonsTableData = [];
      data.lessons.forEach((lesson) => {
        lesson.athletes.forEach((a) => {
          lessonsTableData.push([
            lesson.date,
            lesson.start_time,
            a.athlete_name,
            `€ ${a.venue_amount.toFixed(2)}`,
            a.venue_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE',
          ]);
        });
      });

      autoTable(doc, {
        head: [['Data', 'Ora', 'Atleta', 'Importo', 'Stato']],
        body: lessonsTableData,
        startY: 35,
        theme: 'grid',
        headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
        styles: { fontSize: 8 },
      });

      const filename = `report-affitti-${data.venue.name.replace(/\s+/g, '-')}.pdf`;

      await downloadPDF(doc, filename, {
        title: 'Report Affitti',
        text: 'Report pronto: scegli dove salvarlo o condividerlo',
      });

      toast.success('Report pronto!', { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error('Errore durante l’esportazione del report', { id: toastId });
    }
  };

  const exportAthletePDF = async (athleteData) => {
    if (!data) return;

    const toastId = `export-athlete-pdf-${athleteData.athlete_id}`;
    try {
      toast.loading('Creo il PDF atleta...', { id: toastId });

      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text(`Affitti ${data.venue.name}`, 14, 20);

      doc.setFontSize(12);
      doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);
      doc.setFontSize(14);
      doc.text(`Atleta: ${athleteData.athlete_name}`, 14, 43);

      doc.setFontSize(12);
      doc.text(`Totale Lezioni: ${athleteData.lessons_count}`, 14, 56);
      doc.text(`Totale Affitti: € ${athleteData.total_venue_cost.toFixed(2)}`, 14, 63);
      doc.text(`Pagato: € ${athleteData.paid_venue_cost.toFixed(2)}`, 14, 70);
      doc.text(
        `Da Pagare: € ${(athleteData.total_venue_cost - athleteData.paid_venue_cost).toFixed(2)}`,
        14,
        77
      );

      // Filtra le lezioni per questo atleta
      const athleteLessons = [];
      data.lessons.forEach((lesson) => {
        const athleteInLesson = lesson.athletes.find((a) => a.athlete_id === athleteData.athlete_id);
        if (athleteInLesson) {
          athleteLessons.push([
            lesson.date,
            lesson.start_time,
            `${lesson.duration_minutes} min`,
            `€ ${athleteInLesson.venue_amount.toFixed(2)}`,
            athleteInLesson.venue_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE',
          ]);
        }
      });

      autoTable(doc, {
        head: [['Data', 'Ora', 'Durata', 'Importo', 'Stato']],
        body: athleteLessons,
        startY: 87,
        theme: 'grid',
        headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
        styles: { fontSize: 9 },
      });

      const filename = `affitti-${data.venue.name.replace(/\s+/g, '-')}-${athleteData.athlete_name.replace(/\s+/g, '-')}.pdf`;

      await downloadPDF(doc, filename, {
        title: 'Report Atleta',
        text: 'PDF atleta pronto: scegli dove salvarlo o condividerlo',
      });

      toast.success('Report pronto!', { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error('Errore durante esportazione PDF atleta', { id: toastId });
    }
  };

  if (loading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
        </div>
      </CoachLayout>
    );
  }

  if (!data) return null;

  const totalPagesLessons = Math.ceil(data.lessons.length / ITEMS_PER_PAGE);

  // Ordina le lezioni dalla più recente alla più vecchia
  const sortedLessons = [...data.lessons].sort((a, b) => {
    const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.start_time);
    const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.start_time);
    return dateB - dateA;
  });

  const paginatedLessons = sortedLessons.slice(
    (currentPageLessons - 1) * ITEMS_PER_PAGE,
    currentPageLessons * ITEMS_PER_PAGE
  );

  const totalPagesAthletes = Math.ceil(data.athlete_costs.length / ITEMS_PER_PAGE);

  // Ordina gli atleti in ordine alfabetico
  const sortedAthletes = [...data.athlete_costs].sort((a, b) => a.athlete_name.localeCompare(b.athlete_name));

  const paginatedAthletes = sortedAthletes.slice(
    (currentPageAthletes - 1) * ITEMS_PER_PAGE,
    currentPageAthletes * ITEMS_PER_PAGE
  );

  // Funzione per ottenere le lezioni di un atleta
  const getAthleteLessons = (athleteId) => {
    return data.lessons
      .filter((lesson) => lesson.athletes.some((a) => a.athlete_id === athleteId))
      .sort((a, b) => {
        const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.start_time);
        const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.start_time);
        return dateB - dateA;
      })
      .map((lesson) => {
        const athleteInLesson = lesson.athletes.find((a) => a.athlete_id === athleteId);
        return {
          ...lesson,
          athleteVenueAmount: athleteInLesson?.venue_amount || 0,
          athleteVenueStatus: athleteInLesson?.venue_payment_status || 'pending',
        };
      });
  };

  return (
    <CoachLayout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
            <Button
              onClick={() => navigate('/venues')}
              variant="ghost"
              className="text-zinc-400 hover:text-white p-2 -ml-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 sm:w-8 sm:h-8 text-[#ccff00] flex-shrink-0" />
                <h1 className="text-2xl sm:text-4xl font-black text-white truncate">{data.venue.name}</h1>
              </div>
              <p className="text-zinc-400 mt-1 text-sm sm:text-base">
                Costo fisso: € {data.venue.fixed_cost_per_hour}/h
              </p>
            </div>
          </div>
          <Button
            onClick={exportFullPDF}
            className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl text-sm w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            Esporta Report
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-6 sm:mb-8">
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-xs sm:text-sm">Lezioni</div>
            <div className="text-lg sm:text-2xl font-bold text-white">{data.summary.total_lessons}</div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-xs sm:text-sm">Totale Affitti</div>
            <div className="text-lg sm:text-2xl font-bold text-[#ccff00]">
              € {data.summary.total_venue_cost.toFixed(2)}
            </div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-xs sm:text-sm">Pagato</div>
            <div className="text-lg sm:text-2xl font-bold text-green-500">
              € {data.summary.paid_venue_cost.toFixed(2)}
            </div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-xs sm:text-sm">Da Pagare</div>
            <div className="text-lg sm:text-2xl font-bold text-red-500">
              € {data.summary.unpaid_venue_cost.toFixed(2)}
            </div>
          </div>
        </div>

        <Tabs defaultValue="lessons" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-zinc-900/50 border border-white/10 rounded-xl w-full sm:w-auto">
            <TabsTrigger
              value="lessons"
              className="data-[state=active]:bg-[#ccff00] data-[state=active]:text-black rounded-xl text-xs sm:text-sm flex-1 sm:flex-none"
            >
              Per Lezione ({data.lessons.length})
            </TabsTrigger>
            <TabsTrigger
              value="athletes"
              className="data-[state=active]:bg-[#ccff00] data-[state=active]:text-black rounded-xl text-xs sm:text-sm flex-1 sm:flex-none"
            >
              Per Atleta ({data.athlete_costs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lessons">
            <div className="space-y-3 sm:space-y-4">
              {paginatedLessons.map((lesson) => (
                <div key={lesson.lesson_id} className="glass-card rounded-xl border border-white/10 overflow-hidden">
                  {/* Header della lezione */}
                  <div className="p-3 sm:p-4 border-b border-white/10">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3">
                      <span className="text-[#ccff00] font-bold text-sm sm:text-base">{lesson.date}</span>
                      <span className="text-white text-sm sm:text-base">{lesson.start_time}</span>
                      <span className="text-zinc-400 text-sm">({lesson.duration_minutes} min)</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4">
                      <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
                        <div className="text-zinc-400 text-xs mb-1">Affitto</div>
                        <div className="text-white font-mono font-bold text-sm sm:text-base">
                          €{lesson.total_venue_cost.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
                        <div className="text-zinc-400 text-xs mb-1">Pagato</div>
                        <div className="text-green-500 font-mono font-bold text-sm sm:text-base">
                          €{lesson.paid_venue_cost.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
                        <div className="text-zinc-400 text-xs mb-1">Da pagare</div>
                        <div className="text-red-500 font-mono font-bold text-sm sm:text-base">
                          €{lesson.unpaid_venue_cost.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tabella atleti */}
                  <div className="p-3 sm:p-4">
                    <table className="w-full table-fixed">
                      <thead>
                        <tr className="text-zinc-400 text-xs">
                          <th className="text-left py-2 w-[42%]">Atleta</th>
                          <th className="text-right py-2 w-[33%]">Quota Affitto</th>
                          <th className="text-center py-2 w-[25%]">Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lesson.athletes.map((athlete) => (
                          <tr key={athlete.athlete_id} className="border-t border-white/5">
                            <td className="py-2 pr-2">
                              <button
                                onClick={() => navigate(`/athletes/${athlete.athlete_id}`)}
                                className="text-white hover:text-[#ccff00] text-sm truncate block max-w-full"
                              >
                                {athlete.athlete_name}
                              </button>
                            </td>
                            <td className={`py-2 text-right font-mono text-sm whitespace-nowrap ${
                              athlete.venue_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                            }`}>
                              €{athlete.venue_amount.toFixed(2)}
                            </td>
                            <td className="py-2 text-center whitespace-nowrap">
                              {athlete.venue_payment_status === 'paid' ? (
                                <Button
                                  size="sm"
                                  onClick={() => unmarkAsPaid(lesson.lesson_id, athlete.athlete_id)}
                                  className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => markAsPaid(lesson.lesson_id, athlete.athlete_id)}
                                  className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg"
                                >
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPagesLessons > 1 && (
                <div className="flex items-center justify-center space-x-2 sm:space-x-4 mt-4">
                  <Button
                    onClick={() => setCurrentPageLessons((p) => Math.max(1, p - 1))}
                    disabled={currentPageLessons === 1}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <ChevronLeft className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Indietro</span>
                  </Button>
                  <span className="text-zinc-400 text-xs sm:text-sm">
                    {currentPageLessons} / {totalPagesLessons}
                  </span>
                  <Button
                    onClick={() => setCurrentPageLessons((p) => Math.min(totalPagesLessons, p + 1))}
                    disabled={currentPageLessons === totalPagesLessons}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <span className="hidden sm:inline">Avanti</span>
                    <ChevronRight className="w-4 h-4 sm:ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="athletes">
            <div className="space-y-3">
              {paginatedAthletes.map((athlete) => {
                const isExpanded = expandedAthlete === athlete.athlete_id;
                const athleteLessons = isExpanded ? getAthleteLessons(athlete.athlete_id) : [];

                return (
                  <div key={athlete.athlete_id} className="glass-card rounded-xl border border-white/10 overflow-hidden">
                    {/* Header atleta cliccabile */}
                    <div
                      className="p-3 sm:p-4 cursor-pointer hover:bg-zinc-900/50 transition-colors"
                      onClick={() => setExpandedAthlete(isExpanded ? null : athlete.athlete_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-[#ccff00]" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-zinc-400" />
                          )}
                          <div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/athletes/${athlete.athlete_id}`);
                              }}
                              className="text-white hover:text-[#ccff00] font-bold text-sm sm:text-base"
                            >
                              {athlete.athlete_name}
                            </button>
                            <div className="text-zinc-400 text-xs">{athlete.lessons_count} lezioni</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="text-white font-mono font-bold text-sm">€{athlete.total_venue_cost.toFixed(2)}</div>
                            <div className="text-xs">
                              <span className="text-green-500">€{athlete.paid_venue_cost.toFixed(2)}</span>
                              <span className="text-zinc-500 mx-1">/</span>
                              <span className="text-red-500">€{(athlete.total_venue_cost - athlete.paid_venue_cost).toFixed(2)}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportAthletePDF(athlete);
                            }}
                            variant="outline"
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8 px-2 text-xs rounded-lg"
                          >
                            <Download className="w-3 h-3 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Elenco lezioni espanso */}
                    {isExpanded && athleteLessons.length > 0 && (
                      <div className="border-t border-white/10 p-3 sm:p-4 bg-zinc-900/30">
                        <div className="text-zinc-400 text-xs mb-3 font-medium">Lezioni e Quote Affitto</div>
                        <div className="space-y-2">
                          {athleteLessons.map((lesson) => (
                            <div
                              key={lesson.lesson_id}
                              className="flex items-center justify-between p-2 sm:p-3 bg-zinc-900/50 rounded-lg border border-white/5"
                            >
                              <div className="flex items-center space-x-3">
                                <div>
                                  <div className="text-white text-sm font-medium">{lesson.date}</div>
                                  <div className="text-zinc-400 text-xs">
                                    {lesson.start_time} • {lesson.duration_minutes} min
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="text-right">
                                  <div className={`font-mono text-sm ${
                                    lesson.athleteVenueStatus === 'paid' ? 'text-green-500' : 'text-red-500'
                                  }`}>€{lesson.athleteVenueAmount.toFixed(2)}</div>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded ${
                                      lesson.athleteVenueStatus === 'paid'
                                        ? 'bg-green-900/30 text-green-500'
                                        : 'bg-red-900/30 text-red-500'
                                    }`}
                                  >
                                    {lesson.athleteVenueStatus === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE'}
                                  </span>
                                </div>
                                {lesson.athleteVenueStatus === 'paid' ? (
                                  <Button
                                    size="sm"
                                    onClick={() => unmarkAsPaid(lesson.lesson_id, athlete.athlete_id)}
                                    className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg"
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => markAsPaid(lesson.lesson_id, athlete.athlete_id)}
                                    className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg"
                                  >
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Pagination */}
              {totalPagesAthletes > 1 && (
                <div className="flex items-center justify-center space-x-2 sm:space-x-4 mt-4">
                  <Button
                    onClick={() => setCurrentPageAthletes((p) => Math.max(1, p - 1))}
                    disabled={currentPageAthletes === 1}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <ChevronLeft className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Indietro</span>
                  </Button>
                  <span className="text-zinc-400 text-xs sm:text-sm">
                    {currentPageAthletes} / {totalPagesAthletes}
                  </span>
                  <Button
                    onClick={() => setCurrentPageAthletes((p) => Math.min(totalPagesAthletes, p + 1))}
                    disabled={currentPageAthletes === totalPagesAthletes}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <span className="hidden sm:inline">Avanti</span>
                    <ChevronRight className="w-4 h-4 sm:ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        <div className="mt-6 sm:mt-8 flex flex-row gap-3 sm:gap-4 justify-between">
          <Button
            onClick={handleEdit}
            className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
          >
            Modifica
          </Button>
          <Button
            onClick={handleDelete}
            variant="destructive"
            className="flex-1 rounded-xl"
          >
            Elimina
          </Button>
        </div>
      </div>
    </CoachLayout>
  );
}
